<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useStudentScreen } from '../../useStudentScreen'

const pages = [
  {
    target: '[data-course-card]',
    pos: 'top',
    title: 'Переключение курсов и магазин для родителей',
    body: 'Доступ к магазину только после ввода электронной почты родителя.'
  },
  {
    target: '[data-page-name="audio"]',
    pos: 'top',
    title: 'Аудиоуроки',
    body: 'Ребёнок учится понимать язык на слух. Знакомится с новым материалом, повторяет и закрепляет пройденный.'
  },
  {
    target: '[data-page-name="songs"]',
    pos: 'top',
    title: 'Песни',
    body: 'Каждая песня включает в себя устойчивые выражения и грамматические структуры английского языка. Плейлист составлен так, что ребёнку легко выучить эти песни.'
  },
  {
    target: '[data-page-name="games"]',
    pos: 'top',
    title: 'Приложения',
    body: 'Игровые приложения для тренировки языкового материала.'
  },
  {
    target: '[data-page-name="video"]',
    pos: 'bottom',
    title: 'Видеоуроки',
    body: 'Помогают разобраться в материале аудиоурока, в игровой форме учат держать внимание на речи.'
  },
  {
    target: '[data-page-name="avatar"]',
    pos: 'bottom',
    title: 'Кабинет аватара',
    body: 'Общение с виртуальным другом помогает многократно прослушать ключевые структуры языка, повышает заинтересованность ребенка.'
  },
  {
    target: '[data-page-name="shop"]',
    pos: 'bottom',
    title: 'Магазин аватара',
    body: 'Возможность потратить заработанные за успехи в обучении звёздочки на покупки для аватара. Способствует формированию учебной мотивации.'
  },
  {
    target: '[data-page-name="roundup"]',
    pos: 'bottom',
    title: 'Контрольные уроки',
    body: 'Показывают прогресс и помогают понять, что уже получилось, а что стоит повторить.'
  }
]

type Page = {
  target: string
  pos: 'top' | 'bottom'
  title: string
  body: string
}

const { isMobileLocal } = useStudentScreen()

const isOpen = ref(false)
const currentPageIndex = ref<number | null>(null)
const popup = useTemplateRef<HTMLElement>('popup')
const beak = useTemplateRef<HTMLElement>('beak')

const currentPage = computed<Page | Record<string, never>>(() => {
  if (currentPageIndex.value === null) return {}
  return pages[currentPageIndex.value] || {}
})
const isFirstPage = computed(() => currentPageIndex.value === 0)
const isLastPage = computed(() => currentPageIndex.value === pages.length - 1)
const pageIndicator = computed(() => `${(currentPageIndex.value ?? 0) + 1} из ${pages.length}`)

const getOrientedRect = (element: Element) => {
  const rect = element.getBoundingClientRect()
  const isPortrait = window.matchMedia('(orientation: portrait)').matches

  if (!isPortrait) return rect

  return {
    top: window.innerWidth - rect.right,
    right: rect.bottom,
    bottom: window.innerWidth - rect.left,
    left: rect.top,
    width: rect.height,
    height: rect.width
  }
}

const updatePosDesktop = () => {
  if (!popup.value || !beak.value || currentPageIndex.value === null) return
  const page = pages[currentPageIndex.value]

  const targetElement = document.querySelector(page.target)
  if (!targetElement) return

  const elRect = targetElement.getBoundingClientRect()
  const viewport = {
    width: document.documentElement.clientWidth,
    height: document.documentElement.clientHeight
  }

  popup.value.removeAttribute('style')
  beak.value.removeAttribute('style')

  if (page.pos === 'top') {
    popup.value.style.top = `${elRect.bottom + 24}px`
    popup.value.style.left = `${elRect.left}px`

    beak.value.style.top = '-10px'
    beak.value.style.left = '48px'
  }

  if (page.pos === 'bottom') {
    popup.value.style.bottom = `${viewport.height - (elRect.bottom - elRect.height) + 24}px`
    popup.value.style.right = `${viewport.width - elRect.right}px`

    beak.value.style.bottom = '-10px'
    beak.value.style.right = '48px'
  }
}

const updatePosMobile = async () => {
  if (!popup.value || !beak.value || currentPageIndex.value === null) return
  const page = pages[currentPageIndex.value]

  const targetElement = document.querySelector(page.target)
  const gridElement = document.querySelector('[data-nav-grid]')
  if (!targetElement || !gridElement) return

  const elRect = getOrientedRect(targetElement)
  const gridRect = getOrientedRect(gridElement)
  const viewport = {
    width: document.documentElement.clientWidth,
    height: document.documentElement.clientHeight
  }

  if (window.matchMedia('(orientation: portrait)').matches) {
    viewport.width = document.documentElement.clientHeight
    viewport.height = document.documentElement.clientWidth
  }

  popup.value.removeAttribute('style')
  beak.value.removeAttribute('style')

  const targetCentreX = elRect.left + elRect.width / 2

  if (targetCentreX > viewport.width / 2) {
    popup.value.style.right = `${viewport.width - gridRect.right}px`
  } else {
    popup.value.style.left = `${gridRect.left}px`
  }

  await nextTick()
  const popupRect = getOrientedRect(popup.value)

  beak.value.style.left = `${targetCentreX - popupRect.left - 12}px`

  if (page.pos === 'top') {
    popup.value.style.top = `${elRect.bottom + 24}px`
    popup.value.style.bottom = `${viewport.height - gridRect.bottom}px`
    beak.value.style.top = '-10px'
  }

  if (page.pos === 'bottom') {
    popup.value.style.bottom = `${elRect.top}px`
    popup.value.style.top = `${gridRect.top}px`
    beak.value.style.bottom = '-10px'
  }
}

const updatePosition = () => {
  if (!isOpen.value) return

  if (isMobileLocal.value) {
    void updatePosMobile()
    return
  }

  updatePosDesktop()
}

const $open = () => {
  isOpen.value = true
  currentPageIndex.value = 0
}

defineExpose({ $open })

watch(currentPageIndex, () => {
  updatePosition()
})

const onResize = () => {
  updatePosition()
}

const prevPage = () => {
  if (currentPageIndex.value === null || currentPageIndex.value <= 0) return
  currentPageIndex.value--
}

const nextPage = () => {
  if (currentPageIndex.value === null || currentPageIndex.value >= pages.length - 1) return
  currentPageIndex.value++
}

onMounted(() => {
  window.addEventListener('resize', onResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})
</script>

<template>
  <article v-show="isOpen" ref="popup" class="onboarding-popup">
    <a
      class="onboarding-popup__close-btn"
      @click="
        () => {
          isOpen = false
        }
      "
    >
      <v-icon>mdi-close</v-icon>
    </a>

    <h1 class="text-body-bold pr-2 mt-0" v-text="currentPage.title" />
    <p :class="{ 'text-caption flex-spacer': isMobileLocal }" v-text="currentPage.body" />
    <div class="d-flex align-center">
      <span class="text-body-bold flex-spacer" v-text="pageIndicator" />

      <v-button
        v-if="!isFirstPage"
        :mode="isMobileLocal ? 'outlined small' : 'outlined'"
        class="ml-1"
        @click="prevPage"
      >
        Назад
      </v-button>

      <v-button v-if="isLastPage" :mode="isMobileLocal ? 'filled small' : 'filled'" class="ml-1" @click="() => {}">
        Понятно
      </v-button>
      <v-button v-else :mode="isMobileLocal ? 'filled small' : 'filled'" class="ml-1" @click="nextPage">
        Далее
      </v-button>
    </div>

    <span ref="beak" class="onboarding-popup__beak" />
  </article>
</template>

<style lang="scss" scoped>
@use '~/styles/pages/student2/screen' as s2;

.onboarding-popup {
  background: $blue;
  color: white;
  border-radius: 12px;

  transition: inset 0.1s ease;
  padding: 16px;
  position: fixed;
  z-index: 1000;
  width: 320px;

  @include s2.mobile {
    width: unset;
    max-width: 50vmax;
    padding: 12px 24px;
    display: flex;
    flex-direction: column;

    & > * + * {
      margin-top: 4px;
    }
  }

  &__beak {
    margin: 0;
    background: $blue;
    width: 24px;
    height: 24px;
    border-radius: 4px;

    position: absolute;
    transform: rotate(-45deg);
    z-index: -1;

    transition: inset 0.1s ease;
  }

  &__close-btn {
    position: absolute;
    width: 48px;
    height: 48px;
    top: 0;
    right: 0;
    padding: 12px;
  }

  .v-button._filled {
    background: white;
    color: black;
  }

  .v-button._outlined {
    color: white;
  }
}
</style>
